/**
 * The Acknowledgements page's data: one page of policies, worst first, and
 * the totals above them, from the server.
 *
 * Nothing here reads an acknowledgement record. The page used to read every
 * outstanding one in the organization and count them in the browser; the
 * server now keeps one counted summary per policy and hands over a page of
 * those (functions/src/acknowledgements/ackSummary.ts).
 *
 * WHAT IS BEING ASKED lives in the address (?show=overdue&q=…&size=…),
 * so Back, a refresh and a link sent to a colleague all mean the same thing.
 * WHERE IN THE LIST lives in the browser's history entry, as the cursors of
 * the pages passed through, so Back from a policy's report returns to the
 * same page and a pasted link starts at the top.
 */
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { call } from '../../lib/callable';
import { useSearchKey } from '../search/useSearchKey';
import { runSearch } from '../search/useSearch';
import { FILTER_MAX } from '../../types/fieldLimits';

export const ACK_VIEWS = ['all', 'outstanding', 'overdue', 'complete'] as const;
export type AckView = (typeof ACK_VIEWS)[number];
export const PAGE_SIZES = [25, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZES)[number];
/** The most policies a text search names. The server refuses more. */
export const NAMED_MAX = 50;

export interface AckState { view: AckView; text: string; size: PageSize }
export interface AckRow {
  policyId: string; policyNumber: string; policyTitle: string; departmentNames: string[];
  assigned: number; completed: number; outstanding: number; overdue: number; earliestDue: number | null;
}
export interface AckTotals { policies: number; assigned: number; completed: number; outstanding: number; overdue: number; policiesOverdue: number }
interface Cursor { worst: number; id: string }
interface Overview { rows: (AckRow & { worst: number })[]; total: number; hasNext: boolean; next: Cursor | null; totals: AckTotals }

const ID = /^[A-Za-z0-9_-]{1,128}$/;

export function parseAckState(params: URLSearchParams): AckState {
  const show = params.get('show');
  const size = Number(params.get('size'));
  return {
    view: (ACK_VIEWS as readonly string[]).includes(show ?? '') ? show as AckView : 'all',
    text: (params.get('q') ?? '').slice(0, FILTER_MAX),
    size: (PAGE_SIZES as readonly number[]).includes(size) ? size as PageSize : 50,
  };
}

/** Only what differs from the default, so a plain link stays a plain link. */
export function ackParams(s: AckState): URLSearchParams {
  const out = new URLSearchParams();
  if (s.view !== 'all') out.set('show', s.view);
  if (s.text.trim()) out.set('q', s.text.trim());
  if (s.size !== 50) out.set('size', String(s.size));
  return out;
}

interface Place { key: string; cursors: Cursor[] }

export function useAckOverview(orgId: string | null) {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const state = useMemo(() => parseAckState(params), [params]);
  const key = ackParams(state).toString();

  // The pages passed through, valid only for the question they were passed through under.
  const held = (location.state as { ack?: Place } | null)?.ack;
  const cursors = held && held.key === key && Array.isArray(held.cursors) ? held.cursors : [];
  const page = cursors.length;
  const after = cursors[cursors.length - 1] ?? null;
  const searching = state.text.trim().length > 0;

  // With words typed, the search index says which policies; the server is then asked about exactly those.
  const searchKey = useSearchKey(searching && Boolean(orgId));
  const found = useQuery({
    queryKey: ['ack-search', orgId, searchKey.data?.indexName, state.text.trim()],
    enabled: searching && Boolean(searchKey.data),
    queryFn: async () => {
      const r = await runSearch({
        key: searchKey.data!, query: state.text.trim(), facets: {}, hitsPerPage: NAMED_MAX, page: 0, enforceCeiling: true,
        extraFacetFilters: [['status:published']], numericFilters: [],
      });
      return { ids: [...new Set(r.hits.map((h) => h.policyId).filter((id): id is string => typeof id === 'string' && ID.test(id)))], more: r.totalHits > NAMED_MAX };
    },
  });
  const ids = found.data?.ids;

  const overview = useQuery({
    queryKey: ['ack-overview', orgId, state.view, null, state.size, searching ? ids?.join(',') ?? null : null, searching ? null : after],
    enabled: Boolean(orgId) && (!searching || ids !== undefined),
    staleTime: 30_000,
    // The last page stays up while the next page of the SAME list loads. Not across views or filters:
    // rows answering the last question, under the new one, are wrong for as long as they are there.
    placeholderData: (previous, previousQuery) => {
      const k = previousQuery?.queryKey;
      return k && k[2] === state.view && k[4] === state.size && k[5] === (searching ? ids?.join(',') ?? null : null) ? previous : undefined;
    },
    queryFn: () => call<Record<string, unknown>, Overview>('getAcknowledgementOverview', {
      view: state.view, size: state.size,
      ...(searching ? { policyIds: ids! } : { after }),
    }),
  });

  const go = useCallback((next: AckState, place?: Place, replace = false) => {
    const q = ackParams(next).toString();
    navigate({ search: q ? `?${q}` : '' }, { replace, state: place ? { ack: place } : null });
  }, [navigate]);

  const data = overview.data;
  const from = data && data.rows.length ? (searching ? 1 : page * state.size + 1) : 0;
  return {
    state, searching,
    rows: data?.rows ?? [], totals: data?.totals ?? null, total: data?.total ?? null,
    from, to: data ? from + data.rows.length - (data.rows.length ? 1 : 0) : 0,
    /** Past the first page with nothing on it: the list got shorter since this page was reached. */
    pastTheEnd: !searching && page > 0 && Boolean(data) && data!.rows.length === 0,
    first: () => go(state, undefined, true),
    hasPrevious: !searching && page > 0, hasNext: !searching && Boolean(data?.hasNext),
    /** A search found more policies than it names; only the best matches are listed. */
    searchCut: searching && Boolean(found.data?.more),
    isLoading: (overview.isLoading && !data) || (searching && (searchKey.isLoading || found.isLoading)),
    isUpdating: overview.isFetching && Boolean(data) && overview.isPlaceholderData,
    error: overview.error ?? found.error ?? searchKey.error ?? null,
    retry: () => void overview.refetch(),
    /** Change what is asked. The list starts again from the top. */
    change: (patch: Partial<AckState>, opts: { replace?: boolean } = {}) => go({ ...state, ...patch }, undefined, opts.replace),
    next: () => { if (data?.next) go(state, { key, cursors: [...cursors, data.next] }); },
    previous: () => go(state, { key, cursors: cursors.slice(0, -1) }),
    /** For a link to a policy's report, so Back returns to this page of this list. */
    hereState: { ack: { key, cursors } },
  };
}
